import z from 'zod';
import { Draft } from '../../common';
import { getAddressSchema } from '../../address';
import { IFieldErrors, ValidationError } from '../../validation';
import { IAIA } from './interface';
import { MaterialSchema } from '../../file';
import { DateSchema, EmailSchema } from '../../validation';

/**
 * All non-conditional fields
 */
const Level1Base = z.object({
  name: z.string().min(1),
  email: EmailSchema,
  phone: z.string().min(7),
  fax: z.string().min(7).optional(),
  website: z.string().url('Invalid url entered, must follow format https://your-site.com'),
  existingOrg: z.boolean(),
  sharedOrgAddress: z.boolean(),
  type: z.array(z.enum(['Non-Profit', 'After-School Program', 'Community Engagement Program', 'Community Center', 'Other'])).min(1, 'Must select at least 1 institution type'),
  studentCount: z.number().nonnegative(),
  establishedDate: DateSchema,
  reasonForApplication: z.string().min(1),
  isCBReferral: z.boolean(),
  supportFiles: z.array(MaterialSchema).min(1),
});

const Level2Base = Level1Base.extend({
  hasTestPrep: z.boolean() // Level 2
});

/**
 * Conditional fields but defined as a schema that's required and can be appended to be optional
 * in the event the field is not needed
 */
const existingOrgInfo = z.string().min(1);
const sharedOrgAddressInfo = z.string().min(1);
const typeOther = z.string().min(1);
const referral = z.object({
  name: z.string().min(1),
  email: EmailSchema
});

const testPrep = z.object({ // Level 2
  description: z.string().min(1),
  allowOutsideStudents: z.boolean(),
  hasAdditionalCompensation: z.boolean()
});

const staffBase = z.object({ // level 2
  count: z.number().nonnegative(),
  hasBackgroundChecks: z.boolean()
});
const noBackgroundCheckReason = z.string().min(1);

const examSecurityBase = z.object({ //Level 2
  heldAtVisitingAddress: z.boolean(),
  materialReceivedBy: z.string().min(1),
  materialReceivedAt: z.enum(['Main Office', 'Loading Dock', 'Other']),
  materialStorageLocation: z.string().min(1),
  isMaterialStorageLocked: z.boolean(),
  supervisor: z.string().min(1),
  allowNonEmployedAccess: z.boolean(),
  hasDesignatedTestingRooms: z.boolean()
});

const heldAtShippingAddress = z.boolean();
const materialReceivedAtOther = z.string();
const hasTestingRoomPowerWifi = z.boolean();

/**
 * Validation Schema Generator
 *
 * This is a common piece of export from a standard schema file.  This function operates as
 * a protected method that should only be used to build parent or child schemas in combination
 * with other standard validation.  It should never be exposed to service logic.  The other
 * method `validate()` should be the expected interface when used in business logic.
 * This helps isolate the scope of impact of the underlying validation library.  None of our
 * service logic should change in the event we swap the validation library.
 */
export const getSchema = (appliedLevel: '1' | '2', update: Draft<IAIA>) => {
  const { visitingAddress, shippingAddress } = update;
  const VisitingAddressSchema = getAddressSchema(visitingAddress?.countryIsoCd);
  const ShippingAddressSchema = getAddressSchema(shippingAddress?.countryIsoCd);

  if (appliedLevel === '2') {
    const staff = staffBase.extend({
      noBackgroundCheckReason: update.staff?.hasBackgroundChecks ? noBackgroundCheckReason.optional() : noBackgroundCheckReason
    });

    const examSecurity = examSecurityBase.extend({
      heldAtShippingAddress: update.examSecurity?.heldAtVisitingAddress ? heldAtShippingAddress.optional() : heldAtShippingAddress,
      materialReceivedAtOther: update.examSecurity?.materialReceivedAt === 'Other' ? materialReceivedAtOther : materialReceivedAtOther.optional(),
      hasTestingRoomPowerWifi: update.examSecurity?.hasDesignatedTestingRooms ? hasTestingRoomPowerWifi : hasTestingRoomPowerWifi.optional()
    });

    return Level2Base.extend({
      visitingAddress: VisitingAddressSchema,
      shippingAddress: update.examSecurity?.heldAtShippingAddress ? ShippingAddressSchema : ShippingAddressSchema.optional(),
      existingOrgInfo: update.existingOrg ? existingOrgInfo : existingOrgInfo.optional(),
      sharedOrgAddressInfo: update.sharedOrgAddress ? sharedOrgAddressInfo : sharedOrgAddressInfo.optional(),
      typeOther: update.type?.includes('Other') ? typeOther : typeOther.optional(),
      referral: update.isCBReferral ? referral : referral.optional(),
      testPrep: update.hasTestPrep ? testPrep : testPrep.optional(),
      staff: staff,
      examSecurity: examSecurity
    });
  } else {
    return Level1Base.extend({
      visitingAddress: VisitingAddressSchema,
      shippingAddress: ShippingAddressSchema.optional(),
      existingOrgInfo: update.existingOrg ? existingOrgInfo : existingOrgInfo.optional(),
      sharedOrgAddressInfo: update.sharedOrgAddress ? sharedOrgAddressInfo : sharedOrgAddressInfo.optional(),
      typeOther: update.type?.includes('Other') ? typeOther : typeOther.optional(),
      referral: update.isCBReferral ? referral : referral.optional()
    });
  }
};

/**
 * Execution of Validation Rules
 *
 * The major expectation from the schema file.  This is the true public export
 * which will commonize the input and output into an application driven abstraction.
 * If this is the only function to reach the service layer, replacing the library will
 * be far more approachable as any logic leaning on this will not have proprietary output
 * in either return type or exception
 */
export const validate = (appliedLevel: '1' | '2', update: Draft<IAIA>) => {
  const schema = getSchema(appliedLevel || '1', update);
  try {
    // Ensure there are changes from the original record
    return schema.parse(update);
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();
      throw new ValidationError(errors);
    }
    throw new Error(error?.message || '');
  }
};
